import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useAuth } from '../../contexts/auth-context'
import makeAuthorizedRequest from '../../core/api'
// Import modern, vibrant icons from different icon sets for visual variety
import {
  FaHome,
  FaEdit,
  FaCube,
  FaStar,
  FaChevronRight,
  FaChevronLeft,
  FaStudiovinari,
  FaChartBar,
  FaImages,
  FaLayerGroup,
} from 'react-icons/fa'
import { BiLayerPlus } from 'react-icons/bi'
import { IoColorPaletteSharp } from 'react-icons/io5'
import { Link } from 'react-router-dom'
import ProjectSelector from '../../components/ProjectSelector'
import NoProjectsPrompt from '../../components/NoProjectsPrompt'
import { useBrandFilter } from '../../contexts/brand-filter-context'
import './Sidebar.css'

// How many projects the sidebar navigation lists. Kept small deliberately:
// this is a menu, and the full list lives on the projects gallery page.
const SIDEBAR_PROJECT_LIMIT = 50

const Sidebar = ({ showSidebar, setShowSidebar, setShowTitle }) => {
  const [activeMenu, setActiveMenu] = useState('Home')
  const [projects, setProjects] = useState([])
  const [showPrompt, setShowPrompt] = useState(false)
  const [showProjectSelector, setShowProjectSelector] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const sidebarRef = useRef(null)
  const { idToken, user, isAuthenticated } = useAuth()
  const { brand: division, clearBrand: clearDivision } = useBrandFilter()
  const [activeTooltip, setActiveTooltip] = useState(null)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })

  const setMenu = (item) => {
    if (item === 'Edit') {
      if (projects.length === 0) {
        setShowPrompt(true)
      } else {
        setShowProjectSelector(true)
      }
    } else {
      setActiveMenu(item)
      setShowTitle(item)
    }
  }

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // This runs on every page render. It used to fetch the whole projects
        // table — and the API presigns up to four S3 URLs per row — so the
        // sidebar got slower as projects accumulated. It is a navigation list,
        // not a browse view, so a bounded newest-first page is enough.
        const params = new URLSearchParams()
        if (division) params.set('division', division)
        params.set('limit', String(SIDEBAR_PROJECT_LIMIT))
        params.set('sort', 'newest')
        const endpoint = `/project?${params.toString()}`
        const response = await makeAuthorizedRequest(endpoint, 'GET', null, idToken)

        // Passing limit opts into the paginated shape; still accept a bare
        // array so an older backend keeps working.
        const rows = Array.isArray(response)
          ? response
          : (response && Array.isArray(response.projects) ? response.projects : [])

        let processedProjects = rows.map((project) => {
          if (!project.id && !project.projectId) {
            console.warn('Project without ID found:', project)
          }
          return project
        })

        if (division) {
          processedProjects = processedProjects.filter(
            (p) => p.division && p.division.toUpperCase() === division.toUpperCase()
          )
        }

        setProjects(processedProjects)
      } catch (error) {
        console.error('Error fetching projects:', error)
        setProjects([])
      }
    }

    if (isAuthenticated && idToken) {
      fetchProjects()
    }
  }, [idToken, isAuthenticated, division])

  // Legacy lazy-upsert removed: EdcAuthLambda issues JWTs only for users
  // that already exist in the `users` table (login.js looks them up before
  // signing the token). A valid `idToken` here implies the row exists.

  // Main navigation items (without Settings and Profile)
  const mainNavItems = [
    {
      path: '/',
      label: 'Home',
      Icon: FaHome,
      tooltip: 'Navigate to Home',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/edit',
      label: 'Edit',
      Icon: FaEdit,
      tooltip: 'Open image editor',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/projects',
      label: 'Projects',
      Icon: BiLayerPlus,
      tooltip: 'View projects',
      color: '#74b9ff',
    },
    {
      path: '/designs',
      label: 'Designs',
      Icon: FaImages,
      tooltip: 'Browse all designs',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/canvas',
      label: 'Canvas',
      Icon: IoColorPaletteSharp,
      tooltip: 'Open design canvas',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/viewer',
      label: '3D Viewer',
      Icon: FaCube,
      tooltip: '3D Viewer',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/studio',
      label: '3D Studio',
      Icon: FaStudiovinari,
      tooltip: '3D Modeling Studio',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/favorites',
      label: 'Favorites',
      Icon: FaStar,
      tooltip: 'View favorite designs',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/batch-jobs',
      label: 'Batch Jobs',
      Icon: FaLayerGroup,
      tooltip: 'Manage scheduled batch generation',
      color: '#74b9ff', // matches footer accent
    },
    {
      path: '/admin/surveys',
      label: 'Surveys',
      Icon: FaChartBar,
      tooltip: 'Manage surveys and view statistics',
      color: '#74b9ff', // matches footer accent
    },
  ]

  useEffect(() => {
    // Hide the sidebar when 'Studio' is selected
    if (activeMenu === 'studio' || activeMenu === 'viewer') {
      setShowSidebar(false)
    }
  }, [activeMenu, setShowSidebar])

  const toggleExpand = () => {
    const newExpandedState = !isExpanded
    setIsExpanded(newExpandedState)

    // Dispatch custom event for App.jsx to listen to
    const event = new CustomEvent('sidebarExpansionChanged', {
      detail: { expanded: newExpandedState },
    })
    window.dispatchEvent(event)
  }

  // Emit initial sidebar expansion state on component mount
  useEffect(() => {
    const event = new CustomEvent('sidebarExpansionChanged', {
      detail: { expanded: isExpanded },
    })
    window.dispatchEvent(event)
  }, [])

  // Function to handle tooltip display
  const handleTooltip = (e, tooltip, show) => {
    if (show) {
      const rect = e.currentTarget.getBoundingClientRect()
      setTooltipPosition({
        top: rect.top + rect.height / 2 - 10,
        left: rect.right + 15,
      })
      setActiveTooltip(tooltip)
    } else {
      setActiveTooltip(null)
    }
  }

  // Debug sidebar visibility changes
  useEffect(() => {
    console.log('Sidebar: visibility changed to:', showSidebar)

    // Add an event listener to track Escape key presses
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && window.location.hash.includes('/canvas')) {
        console.log('Sidebar: Escape key detected, should toggle visibility')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [showSidebar])

  // Detect window size changes
  useEffect(() => {
    const handleResize = () => {
      // Auto-collapse sidebar on small screens
      if (window.innerWidth <= 768 && isExpanded) {
        setIsExpanded(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isExpanded])

  return (
    <>
      {/* Fixed position tooltip */}
      {activeTooltip && (
        <div
          className="tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            opacity: 1,
          }}
        >
          {activeTooltip}
        </div>
      )}

      <div
        ref={sidebarRef}
        className={`sidebar ${showSidebar ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-1 z-50 ${isExpanded ? 'w-60' : 'w-16'
          } rounded-lg left-2 top-2 bottom-2 bg-gray-700/[0.8] overflow-hidden`}
        onMouseLeave={() => setActiveTooltip(null)}
        onClick={(e) => {
          // Prevent clicks on sidebar from bubbling to the backdrop
          e.stopPropagation()
        }}
        data-canvas-mode={window.location.hash.includes('/edit').toString()}
        style={{
          zIndex: 9990, // Very high z-index to ensure sidebar is above all edit page elements
          pointerEvents: 'auto', // Ensure sidebar receives mouse events
          visibility: showSidebar ? 'visible' : 'hidden', // Use visibility property
          transition: 'transform 0.2s cubic-bezier(0.25, 1, 0.5, 1), width 0.2s cubic-bezier(0.25, 1, 0.5, 1), visibility 0s linear',
          transitionDelay: showSidebar ? '0s' : '0s, 0s, 0.2s', // Delay visibility hidden until slide out completes
          opacity: 1, // Always keep full opacity
          top: '64px !important',
          height: 'calc(100vh - 64px) !important',
          willChange: 'transform, width',
        }}
      >
        {/* Close Button - Only shown in Canvas mode */}
        {window.location.hash.includes('/canvas') && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              // Uncomment this if you want the X button to work again
              // setShowSidebar(false);
              console.log('Close button clicked, but disabled in canvas mode - use ESC to toggle')
            }}
            className="absolute right-2 top-2 z-10 bg-red-500/80 hover:bg-red-600 rounded-full p-1.5 text-white hover:text-white transition-all shadow-lg flex items-center justify-center"
            style={{ width: '24px', height: '24px' }}
          >
            ✕
          </button>
        )}

        {/* Expand/Collapse Button - Not shown in Canvas mode */}
        {!window.location.hash.includes('/canvas') && isExpanded && (
          <button
            onClick={() => {
              setActiveTooltip(null)
              toggleExpand()
            }}
            className="sidebar-expand-btn absolute right-2 top-2 z-10 rounded-full p-1.5 text-white transition-all shadow-lg flex items-center justify-center"
            onMouseEnter={(e) => handleTooltip(e, 'Collapse Sidebar', true)}
            onMouseLeave={() => handleTooltip(null, '', false)}
          >
            <FaChevronLeft size={14} />
          </button>
        )}

        {!window.location.hash.includes('/canvas') && !isExpanded && (
          <button
            onClick={toggleExpand}
            className="sidebar-expand-btn sidebar-collapsed w-full py-2 px-1 mt-1 mb-3 text-white rounded-md transition-all flex items-center justify-center"
            aria-label="Expand Sidebar"
          >
            <FaChevronRight size={14} />
          </button>
        )}

        {/* TOP SECTION: Logo + Division Badge */}
        <div className={`${isExpanded ? 'p-4' : 'p-2'}`}>
          {isExpanded && (
            <div className="sidebar-logo-container">
              <Link to="/">
                <img src="logo/short_circuit.png" alt="Logo" className="w-auto h-32 mb-3 dim" />
              </Link>
            </div>
          )}

          {division && (
            <div
              className={`flex items-center ${isExpanded ? 'justify-between px-2 mt-2' : 'justify-center mt-1'}`}
              style={{
                background: 'rgba(116, 185, 255, 0.1)',
                border: '1px solid rgba(116, 185, 255, 0.25)',
                borderRadius: '8px',
                padding: isExpanded ? '0.4rem 0.75rem' : '0.4rem',
                cursor: 'pointer',
              }}
              onClick={clearDivision}
              onMouseEnter={(e) => !isExpanded && handleTooltip(e, `Switch division (${division})`, true)}
              onMouseLeave={() => !isExpanded && handleTooltip(null, '', false)}
              title="Click to switch division"
            >
              <img
                src={division === 'MS-RT' ? 'logo/ford-x-msrt-logo.svg' : 'logo/edc-logo.svg'}
                alt={division}
                style={{
                  height: isExpanded ? '18px' : '14px',
                  width: 'auto',
                  maxWidth: isExpanded ? '100px' : '36px',
                  objectFit: 'contain',
                  filter: division === 'EDC' ? 'brightness(1.8)' : 'none',
                }}
              />
              {isExpanded && (
                <span style={{ color: '#b2bec3', fontSize: '0.7rem', cursor: 'pointer' }}>
                  Switch
                </span>
              )}
            </div>
          )}
        </div>

        {/* MIDDLE SECTION: Navigation Items - height limited with bottom padding for absolute positioned footer */}
        <nav className="h-[calc(100%-180px)] overflow-y-auto px-1 mt-2 mb-[100px]" aria-label="Main navigation">
          <ul className={`${isExpanded ? 'px-4' : 'flex flex-col items-center'}`}>
            {mainNavItems.map((item, index) => {
              if (item.indent && !isExpanded) return null
              return (
                <li key={index} className={item.indent ? 'mb-1' : 'mb-3'}>
                  <Link
                    to={item.path}
                    className={`nav-item flex items-center ${item.indent ? 'p-2 pl-8' : 'p-3'} text-white hover:bg-gray-600/70 rounded-lg transition-all ${activeMenu === item.label ? 'nav-active' : ''
                      } ${isExpanded ? '' : 'justify-center'}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      // "Edit" intercepts navigation: opens ProjectSelector
                      // modal so user picks a project first. /edit has no
                      // standalone route — only /edit/:projectId.
                      if (item.label === 'Edit') {
                        e.preventDefault()
                      }
                      setMenu(item.label)
                    }}
                    aria-label={item.label}
                    aria-current={activeMenu === item.label ? 'page' : undefined}
                    onMouseEnter={(e) => !isExpanded && handleTooltip(e, item.tooltip, true)}
                    onMouseLeave={() => !isExpanded && handleTooltip(null, '', false)}
                  >
                    <item.Icon
                      className={`icon ${isExpanded ? 'mr-3' : ''}`}
                      size={item.indent && isExpanded ? 14 : isExpanded ? 18 : 22}
                      style={{ color: activeMenu === item.label ? item.color : '' }}
                    />
                    {isExpanded && (
                      <span className={`font-medium ${item.indent ? 'text-sm opacity-90' : ''}`}>
                        {item.label}
                      </span>
                    )}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* BOTTOM SECTION: Footer only — profile/settings/logout are owned
            by the staffing-app shell that wraps this frontend. */}
        <div className="sidebar-bottom absolute bottom-0 left-0 right-0">
          {isExpanded && (
            <p className="sidebar-footer-text text-center text-xs p-1">© {new Date().getFullYear()} MS-RT</p>
          )}
        </div>

        <ProjectSelector
          projects={projects}
          isOpen={showProjectSelector}
          onClose={() => setShowProjectSelector(false)}
        />
        <NoProjectsPrompt isOpen={showPrompt} onClose={() => setShowPrompt(false)} />
      </div>
    </>
  )
}

Sidebar.propTypes = {
  showSidebar: PropTypes.bool.isRequired,
  setShowSidebar: PropTypes.func.isRequired,
  setShowTitle: PropTypes.func.isRequired,
}

export default Sidebar
